import { canUseDOM } from '@ps/web-core-utils';
import { takeUntil, takeWhile } from 'rxjs/operators';

import { isServiceAuthOnly } from '@swf/core/models/casino/utils';
import { select } from '@swf/core/store';
import type { WidgetEffectParams } from '@swf/core/store';
import { currencyChange, login, logout } from '@swf/core/utils/actions';

import type { CasinoWidget } from '../types';

const empty = { games: [] };

export async function userUpdates(
  payload: undefined,
  { dispatch, actions, subscribe, rootState, widget }: WidgetEffectParams<CasinoWidget>
) {
  const widgetDispatch = dispatch[widget.uid];
  const clientAuthenticated = select.session.isClientAuthenticated(rootState);
  const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

  if (!clientAuthenticated || !canUseDOM) return;

  if (!widget.hide_on_login) {
    subscribe('/user/queue/casino/favorites')
      .pipe(takeUntil(onLogout))
      .subscribe(widgetDispatch.favorites);
    subscribe('/user/queue/fsb').pipe(takeUntil(onLogout)).subscribe(widgetDispatch.freeSpinBonus);
  }
}

export async function myRacesUpdates<T extends CasinoWidget>(
  payload: undefined,
  params: WidgetEffectParams<T>
) {
  const { actions, widget, dispatch, subscribe, rootState } = params;
  const widgetDispatch = dispatch[widget.uid];
  const clientAuthenticated = select.session.isClientAuthenticated(rootState);
  const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

  if (!clientAuthenticated || !canUseDOM) return;

  subscribe('/user/queue/races')
    .pipe(takeUntil(onLogout))
    .subscribe((data) => {
      widgetDispatch.raceStandingUpdate(data);
    });
}

export async function raceUpdate<T extends CasinoWidget>(
  payload: string,
  params: WidgetEffectParams<T>
) {
  const { actions, widget, dispatch, subscribe, rootState } = params;
  const widgetDispatch = dispatch[widget.uid];
  const state = rootState[widget.uid];
  if (!canUseDOM) return;
  const path = `/topic/race.${payload}`;
  const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;
  const reducer = () => !!state?.races[state.knownRaces[payload]];

  subscribe(path)
    .pipe(takeUntil(onLogout), takeWhile(reducer))
    .subscribe((data) => widgetDispatch.applyRaceUpdate({ id: payload, body: data.body }));
}

export async function clearState<T extends CasinoWidget>(
  payload: undefined,
  params: WidgetEffectParams<T>
) {
  const { widget, dispatch } = params;
  const widgetDispatch = dispatch[widget.uid];
  await widgetDispatch.set({ games: [], subscriptions: {}, loading: false });
}

export async function userLoggedIn(payload: undefined, params: WidgetEffectParams<CasinoWidget>) {
  const { widget, dispatch } = params;
  const widgetDispatch = dispatch[widget.uid];

  await widgetDispatch.fetch();
}

export async function userLoggedOut(payload: undefined, params: WidgetEffectParams<CasinoWidget>) {
  const { widget, dispatch } = params;
  const widgetDispatch = dispatch[widget.uid];
  const { game_service: gameService, logged_in_only: loggedInOnly } = widget;

  loggedInOnly || isServiceAuthOnly(gameService as string)
    ? widgetDispatch.set(empty)
    : await widgetDispatch.fetch();
}

export async function preload(payload: undefined, params: WidgetEffectParams<CasinoWidget>) {
  const { widget, dispatch, actions, rootState } = params;
  const widgetDispatch = dispatch[widget.uid];
  const clientAuthenticated = select.session.isClientAuthenticated(rootState);
  const { logged_in_only: loggedInOnly } = widget;

  (!loggedInOnly || clientAuthenticated) && (await widgetDispatch.fetch());

  currencyChange(actions)?.subscribe(({ state }) => {
    widgetDispatch.set(empty);
    if (!loggedInOnly || select.session.isClientAuthenticated(state)) {
      widgetDispatch.fetch();
    }
  });

  login(actions)?.subscribe(async () => {
    widgetDispatch.userLoggedIn?.();
    widgetDispatch.userUpdates();
  });

  logout(actions)?.subscribe(async () => {
    widgetDispatch.userLoggedOut && widgetDispatch.userLoggedOut();
  });

  if (clientAuthenticated && params) {
    userUpdates(undefined, params);
  }
}
